import {
    Component,
    input,
    output,
    signal,
    effect,
    ViewChild,
    TemplateRef,
    computed,
    model,
    OnInit,
    OnDestroy,
    forwardRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import {
    SelectOption,
    SelectConfig,
    SelectSearchEvent,
    SelectLoadMoreEvent,
    SelectChangeEvent,
} from './select.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { TextCasePipe } from '../../../../@shared/pipes/textCase.pipe';
import { DomSanitizer } from '@angular/platform-browser';
import { IconComponent } from "../icon/icon.component";

@Component({
    selector: 'app-select',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectModule, IconComponent],
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true,
        },
        TextCasePipe
    ],
})
export class SelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
    // Model binding for selected value(s)
    value = model<any>(null);

    // Input signals for configuration
    options = input<SelectOption[]>([]);
    config = input<SelectConfig>({});
    optionLabel = input<string>('label');
    optionValue = input<string>('value');
    optionDisabled = input<string>('disabled');

    // Custom templates
    itemTemplate = input<TemplateRef<any> | null>(null);
    selectedItemTemplate = input<TemplateRef<any> | null>(null);
    headerTemplate = input<TemplateRef<any> | null>(null);
    footerTemplate = input<TemplateRef<any> | null>(null);
    emptyTemplate = input<TemplateRef<any> | null>(null);

    // Output events
    onChange = output<SelectChangeEvent>();
    onSearch = output<SelectSearchEvent>();
    onLoadMore = output<SelectLoadMoreEvent>();
    onShow = output<void>();
    onHide = output<void>();
    onClear = output<void>();

    // Internal state
    searchQuery = signal<string>('');
    filteredOptions = signal<SelectOption[]>([]);
    /**
     * Internal loading state for operations like infinite scroll
     * Combined with config.loading to provide overall loading state
     */
    internalLoading = signal<boolean>(false);
    currentPage = signal<number>(0);
    hasMore = signal<boolean>(true);
    // PrimeNG Select reference
    @ViewChild('selectComponent') selectComponent: any;

    // Subjects for reactive programming
    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    private textCasePipe = inject(TextCasePipe);

    // ControlValueAccessor implementation
    private onChangeFn: (value: any) => void = () => { };
    private onTouchedFn: () => void = () => { };

    // Computed values
    selectConfig = computed(() => {
        const defaultConfig: SelectConfig = {
            placeholder: 'Select an option',
            searchable: true,
            searchPlaceholder: 'Search...',
            backendSearch: false,
            infiniteScroll: false,
            loadMoreThreshold: 10,
            pageSize: 20,
            clearable: true,
            multiple: false,
            disabled: false,
            loading: false,
            filter: true,
            filterMatchMode: 'contains',
            showClear: true,
            virtualScroll: false,
            virtualScrollItemSize: 38,
            appendTo: null,
        };
        return { ...defaultConfig, ...this.config() };
    });

    /**
     * Computed loading state - combines config.loading and internal loading
     * This provides a single source of truth for the loading state
     * - config.loading: Controlled by parent component (e.g., when fetching initial data)
     * - internalLoading: Controlled internally (e.g., for infinite scroll load more)
     */
    isLoading = computed(() => {
        return this.selectConfig().loading || this.internalLoading();
    });

    displayOptions = computed(() => {
        const config = this.selectConfig();
        const opts = this.options();

        // If backend search is enabled, return options as-is
        if (config.backendSearch) {
            return opts;
        }

        // If frontend search and there's a query, filter
        if (config.searchable && !config.backendSearch && this.searchQuery()) {
            return this.filterOptionsFrontend(opts, this.searchQuery());
        }

        return opts;
    });

    constructor(private sanitizer: DomSanitizer) {
        // Setup search debouncing
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((query) => {
                this.handleSearch(query);
            });

        // Effect to update filtered options when options change
        effect(() => {
            const opts = this.options();
            this.filteredOptions.set(opts);
            this.internalLoading.set(false);
        });
    }

    ngOnInit(): void {
        // Initialize component
        // Loading state is now handled by computed signal
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handle value change event
     */
    handleChange(event: any): void {
        // Update the internal value
        this.value.set(event.value);

        // Notify Angular forms of the change
        this.onChangeFn(event.value);
        this.onTouchedFn();

        // Emit the change event
        this.onChange.emit({
            value: event.value,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle search input
     */
    onSearchInput(event: any): void {
        const query = event.filter || '';
        this.searchQuery.set(query);

        const config = this.selectConfig();

        if (config.backendSearch) {
            // Trigger backend search via debounced subject
            this.searchSubject.next(query);
        } else {
            // Frontend filtering is handled by computed signal
            this.filteredOptions.set(this.displayOptions());
        }
    }

    /**
     * Handle backend search
     */
    private handleSearch(query: string): void {
        this.onSearch.emit({
            query,
        });
    }

    /**
     * Frontend filter implementation
     */
    private filterOptionsFrontend(
        options: SelectOption[],
        query: string
    ): SelectOption[] {
        if (!query) return options;

        const config = this.selectConfig();
        const lowerQuery = query.toLowerCase();
        const matchMode = config.filterMatchMode || 'contains';

        return options.filter((option) => {
            const label = option.label?.toLowerCase() || '';

            switch (matchMode) {
                case 'startsWith':
                    return label.startsWith(lowerQuery);
                case 'endsWith':
                    return label.endsWith(lowerQuery);
                case 'contains':
                default:
                    return label.includes(lowerQuery);
            }
        });
    }

    /**
     * Handle infinite scroll or load more
     */
    onScroll(event: any): void {
        const config = this.selectConfig();

        if (!config.infiniteScroll || !this.hasMore() || this.isLoading()) {
            return;
        }

        const element = event.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Check if near bottom (within threshold)
        const threshold = config.loadMoreThreshold || 10;
        const isNearBottom =
            scrollTop + clientHeight >= scrollHeight - threshold * 38; // 38px per item

        if (isNearBottom) {
            this.loadMore();
        }
    }

    /**
     * Load more items
     */
    loadMore(): void {
        const config = this.selectConfig();
        const currentLength = this.options().length;
        const pageSize = config.pageSize || 20;
        const nextPage = this.currentPage() + 1;

        this.currentPage.set(nextPage);
        this.internalLoading.set(true);

        this.onLoadMore.emit({
            first: currentLength,
            last: currentLength + pageSize,
        });
    }

    /**
     * Handle show event
     */
    handleShow(): void {
        this.onShow.emit();
        setTimeout(() => {
            const panel = document.querySelector('.p-select-overlay:not([hidden])');
            if (panel) {
                const filterInput = panel.querySelector('input.p-select-filter') as HTMLInputElement;
                if (filterInput) filterInput.blur();

                const listContainer = panel.querySelector('.p-select-list-container') as HTMLElement;
                if (listContainer && this.selectConfig().infiniteScroll) {
                    this._scrollListener = (e: Event) => this.onScroll(e);
                    listContainer.addEventListener('scroll', this._scrollListener);
                    this._scrollContainer = listContainer;
                }
            }
        }, 0);
    }

    /**
     * Handle hide event
     */
    handleHide(): void {
        if (this._scrollContainer && this._scrollListener) {
            this._scrollContainer.removeEventListener('scroll', this._scrollListener);
            this._scrollContainer = null;
            this._scrollListener = null;
        }
        this.onHide.emit();
    }

    private _scrollContainer: HTMLElement | null = null;
    private _scrollListener: ((e: Event) => void) | null = null;

    /**
     * Handle clear event
     */
    handleClear(): void {
        this.searchQuery.set('');
        this.value.set(null);
        this.onClear.emit();
    }

    /**
     * Public method to update options (useful for parent components)
     */
    updateOptions(newOptions: SelectOption[]): void {
        this.filteredOptions.set(newOptions);
    }

    /**
     * Public method to set loading state
     */
    setLoading(loading: boolean): void {
        this.internalLoading.set(loading);
    }

    /**
     * Public method to set hasMore state for infinite scroll
     */
    setHasMore(hasMore: boolean): void {
        this.hasMore.set(hasMore);
    }

    /**
     * Public method to reset pagination
     */
    resetPagination(): void {
        this.currentPage.set(0);
        this.hasMore.set(true);
    }

    /**
     * Public method to clear search
     */
    clearSearch(): void {
        this.searchQuery.set('');
    }

    /**
     * Check if option is disabled
     */
    isOptionDisabled(option: SelectOption): boolean {
        return option.disabled || false;
    }

    // ControlValueAccessor methods
    /**
     * Write a new value to the element (called by Angular forms)
     */
    writeValue(value: any): void {
        this.value.set(value);
    }

    /**
     * Register a callback function to be called when the control's value changes
     */
    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    /**
     * Register a callback function to be called when the control is touched
     */
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    /**
     * Set the disabled state of the control
     */
    setDisabledState(isDisabled: boolean): void {
        // Note: The disabled state should be managed through the config input
        // This method is required by ControlValueAccessor interface
        // The parent component should update the config.disabled property
    }

    placeholderText = computed(() => {
        return this.selectConfig().placeholder ? this.textCasePipe.transform(this.selectConfig().placeholder || '') : '';
    });

    searchPlaceholderText = computed(() => {
        return this.selectConfig().searchPlaceholder ? this.textCasePipe.transform(this.selectConfig().searchPlaceholder || '') : '';
    });
}